import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useFake } from "../../hooks";
import html2canvas from "html2canvas";
import moment from "moment";
import {
  useAssistance,
  useCameras,
  useChimeMeeting,
  useMicrophone,
  useVehicle,
  useView,
} from "../hooks";
import { PluginView } from "../enums";

export const PluginContext = React.createContext({});

/**
 * The plugin context provider
 * 
 * @param {Object} props The props of the component
 * @returns The plugin context provider
 */
const PluginProvider = ({ children }) => {
  const { onCallStart, onCallEnd } = useFake();
  const { assistance, onAssistanceLeave, assistanceRequestToken } = useAssistance();
  const { stopFetching } = useVehicle();
  const { resetCameras } = useCameras();
  const { resetMicrophone } = useMicrophone();
  const { hasJoined, joinMeeting, leaveMeeting } = useChimeMeeting();
  const { onPluginViewChange, pluginView, resetView } = useView();
  const pluginContentRef = useRef(null);

  /**
   * Handles the plugin open
   */
  const onPluginOpen = useCallback(async () => {
    if (assistance || hasJoined) {
      if (!hasJoined) {
        await joinMeeting();
        onCallStart(assistance);
      }
      onPluginViewChange(PluginView.STANDARD);
    }
  }, [assistance, hasJoined, joinMeeting, onCallStart, onPluginViewChange]);

  /**
   * Handles the plugin close
   */
  const onPluginClose = useCallback(async () => {
    await leaveMeeting();
    onAssistanceLeave();
    stopFetching();
    resetCameras();
    resetMicrophone();
    resetView();
    onCallEnd();
  }, [
    leaveMeeting,
    onAssistanceLeave,
    onCallEnd,
    resetCameras,
    resetMicrophone,
    resetView,
    stopFetching,
  ]);

  useEffect(() => {
    if (!assistanceRequestToken) {
      onPluginClose();
    }
  }, [assistanceRequestToken, onPluginClose])

  /**
   * Handles the plugin screenshot
   */
  const onPluginScreenshot = useCallback(async () => {
    const videoImageElements = ["camera-card", "player", "occupants-card"];

    if (pluginContentRef.current) {
      const elementRect = pluginContentRef.current.getBoundingClientRect();
      const bodyRect = document.body.getBoundingClientRect();
      const canvas = await html2canvas(pluginContentRef.current, {
        allowTaint: true,
        useCors: true,
        foreignObjectRendering: true,
        x: bodyRect.left - elementRect.left,
        y: bodyRect.top - elementRect.top,
        width: elementRect.width,
        height: elementRect.height,
        ignoreElements: (element) =>
          videoImageElements.includes(element.id) ? true : false,
      });

      // Temporary fix due to html2canvas bug with foreignObject and video / images elements
      const canvasContext = canvas.getContext("2d");

      await Promise.all(
        videoImageElements.map(async (videoImageElementId) => {
          const videoImageElement =
            document.getElementById(videoImageElementId);

          if (videoImageElement) {
            const videoImageElementRect =
              videoImageElement.getBoundingClientRect();
            const videoImageElementCanvas = await html2canvas(
              videoImageElement,
              {
                foreignObjectRendering: videoImageElementId === "player",
                allowTaint: true,
              },
            );

            canvasContext.drawImage(
              videoImageElementCanvas,
              videoImageElementRect.left - elementRect.left,
              videoImageElementRect.top - elementRect.top,
              videoImageElementRect.width,
              videoImageElementRect.height,
            );
          }
        }),
      );

      //

      const timestamp = moment().unix();
      const base64image = canvas.toDataURL("image/png");
      const imageTitle = `${assistance.license_plate}-${timestamp}.png`;
      const downloadLink = document.createElement("a");

      downloadLink.href = base64image;
      downloadLink.download = imageTitle;
      downloadLink.click();
    }
  }, [assistance]);

  const contextValue = useMemo(
    () => ({
      onPluginClose,
      onPluginOpen,
      onPluginViewChange,
      onPluginScreenshot,
      pluginView,
    }),
    [
      onPluginClose,
      onPluginOpen,
      onPluginViewChange,
      onPluginScreenshot,
      pluginView,
    ],
  );

  return (
    <PluginContext.Provider value={contextValue}>
      <div ref={pluginContentRef} style={{ flexShrink: 0 }}>
        {children}
      </div>
    </PluginContext.Provider>
  );
};

export default React.memo(PluginProvider);
